// Built-in constraints and the MVP quality policy.
//
// The two quality signals the code already computes — LIX readability and
// claim backing (citation coverage) — folded into the Constraint model as
// `computed` constraints, each with a pure predicate that turns
// its signal into an `Observation` (outcome + Score). Plus one `judged`
// constraint left as a typed seam (`distractor_plausibility`): the core
// cannot answer it, so the shell must fold an attestation; until a judge
// is wired the app emits `constraint_pending` for it.
//
// These are domain VALUES (like the `Format` registry) — a computed
// constraint's predicate is pure, over pure `Core.Quality.*` signals, so
// no infrastructure is needed to define them. User-/org-authored policy
// packs are post-MVP (`docs/model/constraints.md` §Creation); this is
// the built-in bundle.
//
// Thresholds are named constants, documented at the predicate. Pure
// module.
export { citationCoverage, distractorPlausibility, linguisticReadability, observeCitationCoverage, observeReadability, qualityPolicy, READABILITY_FLOOR_LIX, READABILITY_HARD_LIX, READABILITY_TOP_LIX, };
import * as Version from "../versioning/version.js";
import * as Constraint from "./constraint.js";
import * as Observation from "./observation.js";
import * as Policy from "./policy.js";
import * as Score from "./score.js";
// === Computed constraint atoms ===
// Readability: the material reads at a manageable level for a general
// learner. `computed` from Björnsson's LIX.
const linguisticReadability = Constraint.mkConstraint(Constraint.mk("C_linguistic_readability"), "computed", "The material reads at a manageable difficulty (LIX). Very hard prose (LIX ≥ 55) is flagged.");
// Citation coverage: every factual item (question / passage) is backed by a
// knowledge-bank claim. `computed` from `Quality.Support`. Replaced the
// word-overlap constraint (owner decision 2026-10-01): "4 of 5 questions
// are backed" says what to do; an overlap percentage did not.
const citationCoverage = Constraint.mkConstraint(Constraint.mk("C_citation_coverage"), "computed", "Every question and passage is backed by a knowledge-bank claim. Unsupported items are flagged.");
// === Judged constraint atom (the seam) ===
//
// The core cannot decide whether a distractor is a genuine, plausible
// misconception — that is an LLM/human judgement. `judged`: the verdict
// must arrive as a folded attestation. Until a judge is wired the shell
// emits `constraint_pending` for it. The pedagogical rubric it would be
// judged against lives in `Core.Quality.ItemQuality`.
const distractorPlausibility = Constraint.mkConstraint(Constraint.mk("C_distractor_plausibility"), "judged", "Each wrong option is a genuine, plausible misconception (judged — needs an evaluator; not answered by the pure core).");
// === Predicates (pure signal → Observation) ===
// LIX bands (`Core.Quality.Lix`): < 55 is up to "hard"; ≥ 55 is
// "very_hard". A material is satisfied while it stays below very-hard.
// The score is a linear readability measure: 1.0 at/below LIX 45 (the
// "medium" ceiling), falling to 0.0 at LIX 85.
const READABILITY_HARD_LIX = 55;
const READABILITY_TOP_LIX = 45;
const READABILITY_FLOOR_LIX = 85;
const observeReadability = (lix) => {
    const outcome = lix.score < READABILITY_HARD_LIX ? "satisfied" : "violated";
    const span = READABILITY_FLOOR_LIX - READABILITY_TOP_LIX;
    const raw = 1 - Math.max(0, lix.score - READABILITY_TOP_LIX) / span;
    return Observation.measured(linguisticReadability.id, outcome, Score.mk(raw));
};
// The score is the backed fraction over all factual items (1.0 when the
// material makes none — nothing to back). Satisfied iff every item is
// backed.
const observeCitationCoverage = (coverage) => {
    const total = coverage.reduce((n, k) => n + k.total, 0);
    const cited = coverage.reduce((n, k) => n + k.cited, 0);
    return Observation.measured(citationCoverage.id, cited >= total ? "satisfied" : "violated", Score.mk(total === 0 ? 1 : cited / total));
};
// === The MVP quality policy ===
//
// The built-in "material quality" filter over the two computed signals
// plus the judged distractor seam. Severity is per-membership; all are
// `soft` advice. They only WARN at the write boundary (advisory) —
// severity bites only when the export gate is opted on.
const qualityPolicy = Policy.mkPolicy(Policy.mk("P_quality"), Version.v1, [
    Policy.mkMember(linguisticReadability, "soft"),
    Policy.mkMember(citationCoverage, "soft"),
    Policy.mkMember(distractorPlausibility, "soft"),
]);
//# sourceMappingURL=builtins.js.map